import type { AuthTokensResponse, LoginInput, RegisterInput, SessionUser } from '@whn/shared';
import { can, canAtScope, type AccessScope, type Permission } from '@whn/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { api, ApiError, onSessionExpired } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Capability check mirroring the server's. The server remains the boundary. */
  can: (permission: Permission) => boolean;
  /** Capability check that also requires a minimum reach (OWN / TEAM / ALL). */
  canAtScope: (permission: Permission, minScope: AccessScope) => boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const session = useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => api.get<SessionUser>('/auth/me', signal),
    // A 401 here simply means "not signed in" — it is the expected answer on
    // first load, not an error worth retrying.
    retry: false,
    staleTime: 5 * 60_000,
  });

  // The API client tells us when a refresh has definitively failed; drop the
  // cached session so the router sends the user to sign-in.
  React.useEffect(
    () =>
      onSessionExpired(() => {
        queryClient.setQueryData(queryKeys.session, null);
      }),
    [queryClient],
  );

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => api.post<AuthTokensResponse>('/auth/login', input),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.session, data.user),
  });

  const registerMutation = useMutation({
    mutationFn: (input: RegisterInput) => api.post<AuthTokensResponse>('/auth/register', input),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.session, data.user),
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      // Clear everything: cached lists belong to the account that just left.
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session, null);
    },
  });

  const user = (session.data as SessionUser | null | undefined) ?? null;

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: session.isLoading,
      login: async (input) => {
        await loginMutation.mutateAsync(input);
      },
      register: async (input) => {
        await registerMutation.mutateAsync(input);
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      can: (permission) => (user ? can(user.role, permission) : false),
      canAtScope: (permission, minScope) =>
        user ? canAtScope(user.role, permission, minScope) : false,
    }),
    [user, session.isLoading, loginMutation, registerMutation, logoutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/** The signed-in user, for screens that are only reachable when signed in. */
export function useCurrentUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser called outside an authenticated route');
  return user;
}

export { ApiError };
